import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import LogoMark from "@/components/site/LogoMark";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not login";
      toast.error(typeof detail === "string" ? detail : "Could not login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-ivory">
      <div className="hidden lg:flex flex-col justify-between bg-ink text-ivory p-12 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="relative">
          {settings?.logo_url_dark ? (
            <img src={settings.logo_url_dark} alt="Happy Retreats" className="h-16 w-auto" />
          ) : settings?.logo_url ? (
            <LogoMark src={settings.logo_url} dark className="h-16 w-auto" alt="Happy Retreats" />
          ) : (
            <>
              <div className="overline text-ivory/60">Happy Retreats</div>
              <div className="mt-2 font-display text-4xl">Admin</div>
            </>
          )}
        </div>
        <div className="relative">
          <h1 className="font-display text-5xl leading-tight">The control room <em className="italic text-clay">for every page</em>.</h1>
          <p className="mt-6 text-ivory/70 max-w-md font-light">Edit properties, publish stories, monitor leads, and orchestrate tracking — all from one place.</p>
        </div>
        <div className="relative text-xs text-ivory/50">© Happy Retreats CMS</div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="overline">Sign in</div>
          <h2 className="font-display text-4xl md:text-5xl mt-4">Admin login.</h2>
          <p className="text-graphite mt-4 text-sm">Use your Happy Retreats admin credentials.</p>
          <div className="mt-10 space-y-4">
            <div>
              <label className="overline">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2" data-testid="login-email" />
            </div>
            <div>
              <label className="overline">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2" data-testid="login-password" />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary mt-8 w-full disabled:opacity-60" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
